/* WATCHERS V3 — PRODUCT FORM — PF09 WATCHERS DRAWER + CART BUBBLE SYNC */

if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        if (!this.form || !this.variantIdInput) return;

        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));

        /* WATCHERS: never use the old cart-notification UI. */
        this.cart =
          document.querySelector('cart-drawer.watchers-cart') ||
          document.querySelector('cart-drawer');

        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton?.querySelector('span');
        this.pending = false;
        this.hideErrors = this.dataset.hideErrors === 'true';

        if (this.cart && this.submitButton) {
          this.submitButton.setAttribute('aria-haspopup', 'dialog');
        }
      }

      getCart() {
        if (!this.cart || !this.cart.isConnected) {
          this.cart =
            document.querySelector('cart-drawer.watchers-cart') ||
            document.querySelector('cart-drawer');
        }
        return this.cart;
      }

      async syncCartBubble() {
        try {
          const response = await fetch(
            `${window.routes?.cart_url || '/cart'}.js?_watchers=${Date.now()}`,
            {
              method: 'GET',
              credentials: 'same-origin',
              headers: {
                Accept: 'application/json',
                'X-Requested-With': 'XMLHttpRequest',
              },
              cache: 'no-store',
            }
          );

          if (!response.ok) return;

          const cartState = await response.json();
          const count = Number(cartState?.item_count || 0);
          const bubbleLink = document.querySelector('#cart-icon-bubble');

          if (!bubbleLink) return;

          let bubble = bubbleLink.querySelector('.cart-count-bubble');

          if (count <= 0) {
            bubble?.remove();
            return;
          }

          if (!bubble) {
            bubble = document.createElement('div');
            bubble.className = 'cart-count-bubble';
            bubbleLink.appendChild(bubble);
          }

          bubble.innerHTML =
            count < 100
              ? `<span aria-hidden="true">${count}</span><span class="visually-hidden">${count} item${count === 1 ? '' : 's'}</span>`
              : `<span class="visually-hidden">${count} items</span>`;
        } catch (error) {
          console.warn('[WATCHERS Cart Bubble]', error);
        }
      }

      normalizeQuantity(formData) {
        const input = this.form?.querySelector('[name="quantity"]');
        if (!input) return;

        let value = Number(input.value || 1);
        const min = Number(input.min || input.dataset.min || 1);
        const maxRaw = input.max || input.dataset.max || '';
        const max = maxRaw === '' ? null : Number(maxRaw);
        const step = Number(input.step || 1) || 1;

        if (!Number.isFinite(value)) value = Number.isFinite(min) ? min : 1;
        if (Number.isFinite(min)) value = Math.max(value, min);
        if (max !== null && Number.isFinite(max)) value = Math.min(value, max);

        if (Number.isFinite(min) && Number.isFinite(step) && step > 0) {
          const steps = Math.max(0, Math.round((value - min) / step));
          value = min + steps * step;
          if (max !== null && Number.isFinite(max)) value = Math.min(value, max);
        }

        value = Math.max(1, value);
        input.value = String(value);
        formData.set('quantity', String(value));
      }

      async onSubmitHandler(evt) {
        evt.preventDefault();

        if (!this.submitButton || this.pending) return;
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        const cart = this.getCart();

        if (!cart || typeof cart.renderContents !== 'function') {
          this.handleErrorMessage('Unable to open the bag. Please refresh and try again.');
          return;
        }

        this.pending = true;
        this.error = false;
        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', 'true');
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner')?.classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        this.normalizeQuantity(formData);

        const variantId = formData.get('id');

        const sectionIds = cart
          .getSectionsToRender()
          .map((section) => section.id)
          .filter(Boolean);

        formData.set('sections', sectionIds.join(','));
        formData.set('sections_url', window.location.pathname);
        cart.setActiveElement?.(document.activeElement);

        config.body = formData;

        try {
          const fetchResponse = await fetch(`${routes.cart_add_url}`, config);
          const response = await fetchResponse.json();

          if (!fetchResponse.ok || response?.status) {
            publish(PUB_SUB_EVENTS.cartError, {
              source: 'product-form',
              productVariantId: variantId,
              errors: response?.errors || response?.description,
              message: response?.message,
            });

            const description = String(
              response?.description ||
              response?.message ||
              'Unable to add this quantity.'
            );

            this.handleErrorMessage(description);

            const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
            const trulySoldOut = /sold\s*out|unavailable|not\s*available/i.test(description);

            if (soldOutMessage && trulySoldOut) {
              this.error = true;
              this.submitButton.setAttribute('aria-disabled', 'true');
              this.submitButtonText?.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
            }

            return;
          }

          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'product-form',
            productVariantId: variantId,
            cartData: response,
          });

          this.error = false;

          const quickAddModal = this.closest('quick-add-modal');

          if (quickAddModal) {
            document.body.addEventListener(
              'modalClosed',
              async () => {
                requestAnimationFrame(() => cart.renderContents(response));
                await this.syncCartBubble();
              },
              { once: true }
            );
            quickAddModal.hide(true);
          } else {
            /*
              The /cart/add.js response already contains cart-drawer and
              cart-icon-bubble sections rendered from THIS SAME mutation.
              Render that exact response. No second cart request, no race.
            */
            cart.renderContents({
              ...response,
              watchersOpenDrawer: true,
            });

            await this.syncCartBubble();
          }
        } catch (error) {
          console.error('[WATCHERS Product Form]', error);
          this.handleErrorMessage('Unable to update the bag. Please try again.');
        } finally {
          this.pending = false;
          this.submitButton.classList.remove('loading');

          if (!this.error) {
            this.submitButton.removeAttribute('aria-disabled');
          }

          this.querySelector('.loading__spinner')?.classList.add('hidden');
        }
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper ||
          this.querySelector('.product-form__error-message-wrapper');

        if (!this.errorMessageWrapper) return;

        this.errorMessage =
          this.errorMessage ||
          this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage && this.errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (!this.submitButton) return;

        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text && this.submitButtonText) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          if (this.submitButtonText) {
            this.submitButtonText.textContent = window.variantStrings.addToCart;
          }
        }
      }

      get variantIdInput() {
        return this.form?.querySelector('[name=id]') || this.querySelector('form [name=id]');
      }
    }
  );
}
